import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Loader2,
  MessageCircleMore,
  RefreshCw,
  Send,
  Sparkles,
  UserRound,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../config';
import ModalShell from './ModalShell';

const OPERATOR_TEMPLATES = [
  'Здравствуйте! Курьер подъедет в назначенное время.',
  'Ваш заказ готов, можете забирать в пункте выдачи.',
  'Уточните, пожалуйста, удобное время для визита курьера.',
  'Заказ принят в работу, стирка уже началась.',
  'Спасибо за обращение, скоро свяжемся с вами!',
];


const requestTitle = (request) => request?.order_number
  ? `Заказ №${request.order_number}`
  : `Заявка #${request?.request_id || request?.id || '—'}`;

const customerName = (request) => {
  const customer = request?.customer || {};
  return [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.phone || 'Клиент приложения';
};

const formatMoment = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const ClientChatManager = () => {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (selectedId && !messagesLoading) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedId, messages, messagesLoading]);

  const apiRequest = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_URL}/api/client-intake/v1${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const detail = payload?.detail;
      throw new Error(typeof detail === 'string' ? detail : detail?.message || 'Не удалось выполнить действие');
    }
    return payload;
  }, []);

  const loadChats = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const payload = await apiRequest('/chats?limit=200');
      setChats(Array.isArray(payload) ? payload : []);
      setError('');
    } catch (loadError) {
      if (!silent) setError(loadError.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiRequest]);

  const loadMessages = useCallback(async (requestId, { silent = false } = {}) => {
    if (!requestId) return;
    if (!silent) setMessagesLoading(true);
    try {
      const payload = await apiRequest(`/requests/${requestId}/messages`);
      setSelectedRequest(payload.request || null);
      setMessages(payload.items || []);
      setChats((current) => current.map((chat) => (
        Number(chat.request_id) === Number(requestId) ? { ...chat, unread_count: 0 } : chat
      )));
      setError('');
    } catch (loadError) {
      if (!silent) setError(loadError.message);
    } finally {
      if (!silent) setMessagesLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    loadChats();
    const timer = window.setInterval(() => loadChats({ silent: true }), 15000);
    return () => window.clearInterval(timer);
  }, [loadChats]);

  useEffect(() => {
    if (!selectedId) return undefined;
    loadMessages(selectedId);
    const timer = window.setInterval(() => loadMessages(selectedId, { silent: true }), 8000);
    return () => window.clearInterval(timer);
  }, [selectedId, loadMessages]);

  const unreadTotal = useMemo(
    () => chats.reduce((total, chat) => total + Number(chat.unread_count || 0), 0),
    [chats]
  );

  const openChat = (chat) => {
    setSelectedId(chat.request_id);
    setSelectedRequest(chat);
    setMessages([]);
    setDraft('');
    setError('');
  };

  const closeChat = () => {
    setSelectedId(null);
    setSelectedRequest(null);
    setMessages([]);
    setDraft('');
    loadChats({ silent: true });
  };

  const sendMessage = async () => {
    const text = draft.trim();
    if (!selectedId || !text || sending) return;
    setSending(true);
    try {
      const message = await apiRequest(`/requests/${selectedId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ text })
      });
      setMessages((current) => [...current.filter((row) => row.id !== message.id), message]);
      setDraft('');
      setError('');
      loadChats({ silent: true });
    } catch (sendError) {
      setError(sendError.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-sm dark:bg-gray-900">
      <div className="flex flex-col gap-3 border-b border-gray-100 p-4 dark:border-gray-800 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"><MessageCircleMore className="h-5 w-5" /></span>
          <div><h2 className="text-sm font-bold text-gray-900 dark:text-white">Чаты с клиентами</h2><p className="mt-1 text-xs text-gray-500">Один диалог привязан к заявке и продолжает работать после создания заказа.</p></div>
        </div>
        <div className="flex items-center gap-2">
          {unreadTotal > 0 && <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700 dark:bg-red-950/40 dark:text-red-300">Новых: {unreadTotal}</span>}
          <button type="button" onClick={() => loadChats()} disabled={loading} className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Обновить</button>
        </div>
      </div>

      {error && !selectedId && <div className="m-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">{error}</div>}
      {loading ? (
        <div className="flex items-center justify-center p-10 text-sm text-gray-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Загружаем диалоги…</div>
      ) : chats.length ? (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {chats.map((chat) => (
            <button type="button" key={chat.request_id} onClick={() => openChat(chat)} className="flex w-full items-center gap-3 p-3 text-left transition hover:bg-blue-50/40 dark:hover:bg-blue-950/20">
              <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"><UserRound className="h-5 w-5" />{chat.unread_count > 0 && <i className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold not-italic text-white">{chat.unread_count}</i>}</span>
              <span className="min-w-0 flex-1"><span className="flex items-center justify-between gap-2"><strong className="truncate text-sm text-gray-900 dark:text-white">{customerName(chat)}</strong><small className="shrink-0 text-[11px] text-gray-400">{formatMoment(chat.updated_at)}</small></span><span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400"><span>{requestTitle(chat)} · {chat.department?.name || 'Без подразделения'}</span>{chat.status_label && <span className="rounded-full bg-blue-100/70 px-1.5 py-0.2 text-[10px] font-semibold text-blue-800 dark:bg-blue-950/50 dark:text-blue-300">{chat.status_label}</span>}</span><span className={`mt-1 block truncate text-xs ${chat.unread_count > 0 ? 'font-semibold text-gray-800 dark:text-gray-100' : 'text-gray-500'}`}>{chat.latest_message?.sender_kind === 'client' ? '' : 'Вы: '}{chat.latest_message?.text || 'Нет сообщений'}</span></span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
            </button>
          ))}
        </div>
      ) : (
        <div className="p-10 text-center"><CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" /><h3 className="mt-3 text-sm font-semibold text-gray-900 dark:text-white">Диалогов пока нет</h3><p className="mt-1 text-xs text-gray-500">Чат появится, когда клиент отправит первое сообщение по заявке.</p></div>
      )}

      <ModalShell open={Boolean(selectedId)} onClose={closeChat} variant="window" panelClassName="flex h-[min(760px,92vh)] max-w-2xl flex-col overflow-hidden p-0">
        <header className="flex items-start justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <div className="min-w-0"><div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-600"><span>{requestTitle(selectedRequest)}</span>{selectedRequest?.status_label && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 normal-case dark:bg-blue-950/50 dark:text-blue-300">{selectedRequest.status_label}</span>}</div><h3 className="mt-1 truncate font-bold text-gray-900 dark:text-white">{customerName(selectedRequest)}</h3><p className="mt-1 text-xs text-gray-500">{selectedRequest?.customer?.phone || 'Телефон не указан'} · {selectedRequest?.department?.name || 'Подразделение не определено'}</p></div>
          <div className="flex shrink-0 items-center gap-1">{selectedRequest?.worker_order_id && <button type="button" onClick={() => navigate(`/order/${selectedRequest.worker_order_id}`)} className="rounded-xl p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30" aria-label="Открыть заказ"><ExternalLink className="h-5 w-5" /></button>}<button type="button" onClick={closeChat} className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Закрыть"><X className="h-5 w-5" /></button></div>
        </header>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-gray-50 p-4 dark:bg-gray-950">
          {messagesLoading ? <div className="flex items-center justify-center py-12 text-sm text-gray-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Загружаем сообщения…</div> : messages.map((message) => {
            const outgoing = message.sender_kind !== 'client';
            return <div key={message.id} className={`flex ${outgoing ? 'justify-end' : 'justify-start'}`}><div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${outgoing ? 'rounded-br-md bg-blue-600 text-white' : 'rounded-bl-md bg-white text-gray-900 dark:bg-gray-800 dark:text-white'}`}><div className={`mb-1 text-[10px] font-semibold ${outgoing ? 'text-blue-100' : 'text-gray-400'}`}>{message.sender_name || (outgoing ? 'Поддержка' : 'Клиент')}</div><p className="whitespace-pre-wrap break-words">{message.text}</p><div className={`mt-1 text-right text-[10px] ${outgoing ? 'text-blue-100' : 'text-gray-400'}`}>{formatMoment(message.created_at)}</div></div></div>;
          })}
          {!messagesLoading && !messages.length && <div className="py-12 text-center text-sm text-gray-500">В диалоге пока нет сообщений.</div>}
          <div ref={chatEndRef} />
        </div>

        {error && selectedId && <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">{error}</div>}
        <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-100 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-950/50">
          <span className="flex items-center text-[11px] font-medium text-gray-500 mr-1"><Sparkles className="mr-1 h-3 w-3 text-blue-500" />Шаблоны:</span>
          {OPERATOR_TEMPLATES.map((tmpl, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setDraft((current) => current ? `${current} ${tmpl}` : tmpl)}
              className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 transition hover:bg-blue-50 hover:border-blue-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-blue-950/40"
            >
              {tmpl}
            </button>
          ))}
        </div>
        <div className="flex items-end gap-2 border-t border-gray-100 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
          <textarea value={draft} onChange={(event) => setDraft(event.target.value.slice(0, 4000))} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); sendMessage(); } }} rows="2" placeholder="Напишите клиенту…" className="min-h-[44px] flex-1 resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          <button type="button" onClick={sendMessage} disabled={sending || !draft.trim()} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white disabled:opacity-40" aria-label="Отправить">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
        </div>
      </ModalShell>
    </section>
  );
};

export default ClientChatManager;
